import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PortfolioIcon } from '../components/Icons';
import { fetchPortfolio, detectCurrency, fetchPrice, authHeaders } from '../utils/api';

function PortfolioManager() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState([]);
  const [prices, setPrices] = useState({});
  const [view, setView] = useState('account');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadPortfolio(); }, []);

  async function loadPortfolio() {
    const data = await fetchPortfolio();
    setPortfolio(data);
    const priceMap = {};
    for (const h of data) {
      if (!priceMap[h.ticker]) {
        const p = await fetchPrice(h.ticker);
        if (p) priceMap[h.ticker] = p;
      }
    }
    setPrices(priceMap);
  }

  function confirmDelete(ticker, broker) {
    setDeleteConfirm({ ticker, broker });
  }

  async function handleDelete() {
    if (!deleteConfirm) return;
    const { ticker, broker } = deleteConfirm;
    const params = broker ? `?broker=${encodeURIComponent(broker)}` : '';
    await fetch(`/api/portfolio/${encodeURIComponent(ticker)}${params}`, { method: 'DELETE', headers: authHeaders() });
    setDeleteConfirm(null);
    await loadPortfolio();
  }

  const parseBroker = (broker) => {
    if (!broker) return { platform: 'Manual Entry', accountType: '' };
    const parts = broker.split(' — ');
    return { platform: parts[0] || broker, accountType: parts[1] || '' };
  };

  const platformGroups = {};
  portfolio.forEach(h => {
    const { platform, accountType } = parseBroker(h.broker);
    if (!platformGroups[platform]) platformGroups[platform] = {};
    if (!platformGroups[platform][accountType]) platformGroups[platform][accountType] = [];
    platformGroups[platform][accountType].push(h);
  });

  const consolidated = {};
  portfolio.forEach(h => {
    if (!consolidated[h.ticker]) {
      consolidated[h.ticker] = { ticker: h.ticker, shares: 0, totalCost: 0, brokers: [] };
    }
    consolidated[h.ticker].shares += h.shares;
    consolidated[h.ticker].totalCost += h.shares * h.buy_price;
    if (h.broker && !consolidated[h.ticker].brokers.includes(h.broker)) {
      consolidated[h.ticker].brokers.push(h.broker);
    }
  });
  Object.values(consolidated).forEach(c => {
    c.avg_price = c.shares > 0 ? c.totalCost / c.shares : 0;
  });

  const calcValue = (ticker, shares) => {
    const p = prices[ticker];
    if (!p) return null;
    return p.price * shares;
  };

  const calcGainPct = (ticker, buyPrice) => {
    const p = prices[ticker];
    if (!p || !buyPrice) return null;
    return ((p.price - buyPrice) / buyPrice * 100);
  };

  return (
    <div>
      <div className="page-title">
        <PortfolioIcon size={28} color="#00ff9d" />
        <h1>Portfolio Manager</h1>
      </div>
      <p className="hero-subtitle">Track and manage your NASDAQ & TSX holdings</p>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 3, border: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            onClick={() => setView('account')}
            style={{
              padding: '8px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.2s',
              background: view === 'account' ? 'rgba(0,255,157,0.15)' : 'transparent',
              color: view === 'account' ? '#00ff9d' : '#64748b',
            }}
          >By Account</button>
          <button
            onClick={() => setView('consolidated')}
            style={{
              padding: '8px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, transition: 'all 0.2s',
              background: view === 'consolidated' ? 'rgba(0,255,157,0.15)' : 'transparent',
              color: view === 'consolidated' ? '#00ff9d' : '#64748b',
            }}
          >Consolidated</button>
        </div>
        <button className="btn-primary" onClick={() => navigate('/import')} style={{ fontSize: 13, padding: '8px 16px' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: 'middle' }}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Import Holdings
        </button>
      </div>

      {portfolio.length === 0 ? (
        <div className="glass-card empty-state" style={{ padding: '30px 20px' }}>
          <p style={{ color: '#475569' }}>No holdings yet — use Import Holdings to add your first stock!</p>
        </div>
      ) : view === 'account' ? (
        Object.entries(platformGroups).map(([platformName, accountGroups]) => {
          const platformTotal = Object.values(accountGroups).flat().reduce((sum, h) => sum + (calcValue(h.ticker, h.shares) || 0), 0);
          return (
            <div key={platformName} className="glass-card" style={{ marginBottom: 24, padding: '20px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <h3 style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00ff9d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="7" width="18" height="13" rx="2" />
                    <path d="M8 7V5a4 4 0 0 1 8 0v2" />
                  </svg>
                  {platformName}
                </h3>
                {platformTotal > 0 && <span style={{ color: '#00ff9d', fontSize: 14, fontWeight: 600 }}>${platformTotal.toFixed(2)}</span>}
              </div>

              {Object.entries(accountGroups).map(([acctType, holdings]) => {
                const acctTotal = holdings.reduce((sum, h) => sum + (calcValue(h.ticker, h.shares) || 0), 0);
                return (
                  <div key={acctType} style={{ marginBottom: 16 }}>
                    {acctType && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, paddingLeft: 8 }}>
                        <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                          </svg>
                          {acctType}
                        </span>
                        {acctTotal > 0 && <span style={{ color: '#94a3b8', fontSize: 12 }}>${acctTotal.toFixed(2)}</span>}
                      </div>
                    )}
                    {holdings.map(h => {
                      const val = calcValue(h.ticker, h.shares);
                      const gain = calcGainPct(h.ticker, h.buy_price);
                      return (
                        <div className="holding-row" key={`${h.ticker}-${h.broker}`} style={{ marginLeft: acctType ? 12 : 0 }}>
                          <span className="holding-ticker">{h.ticker}</span>
                          <span className="holding-detail">{h.shares} shares</span>
                          <span className="holding-detail">Avg: {h.currency || detectCurrency(h.ticker)} {h.buy_price.toFixed(2)}</span>
                          {val != null && <span className="holding-detail" style={{ color: '#94a3b8' }}>${val.toFixed(2)}</span>}
                          {gain != null && (
                            <span className="holding-detail" style={{ color: gain >= 0 ? '#00ff9d' : '#ff4d4d', fontWeight: 600 }}>
                              {gain >= 0 ? '+' : ''}{gain.toFixed(2)}%
                            </span>
                          )}
                          <button className="btn-danger" onClick={() => confirmDelete(h.ticker, h.broker)}>Remove</button>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })
      ) : (
        <div>
          {Object.values(consolidated).map(c => {
            const val = calcValue(c.ticker, c.shares);
            const gain = calcGainPct(c.ticker, c.avg_price);
            return (
              <div className="holding-row" key={c.ticker}>
                <span className="holding-ticker">{c.ticker}</span>
                <span className="holding-detail">{c.shares} shares</span>
                <span className="holding-detail">Avg: {detectCurrency(c.ticker)} {c.avg_price.toFixed(2)}</span>

                {val != null && <span className="holding-detail" style={{ color: '#94a3b8' }}>${val.toFixed(2)}</span>}
                {gain != null && (
                  <span className="holding-detail" style={{ color: gain >= 0 ? '#00ff9d' : '#ff4d4d', fontWeight: 600 }}>
                    {gain >= 0 ? '+' : ''}{gain.toFixed(2)}%
                  </span>
                )}
                {c.brokers.length > 0 && (
                  <span className="holding-detail" style={{ color: '#64748b', fontSize: 11 }}>
                    {c.brokers.join(', ')}
                  </span>
                )}
              </div>
            );
          })}
          {Object.keys(consolidated).length > 0 && (() => {
            const grandTotal = Object.values(consolidated).reduce((s, c) => s + (calcValue(c.ticker, c.shares) || 0), 0);
            return grandTotal > 0 ? (
              <div style={{ textAlign: 'right', marginTop: 12, color: '#00ff9d', fontWeight: 600, fontSize: 15 }}>
                Grand Total: ${grandTotal.toFixed(2)}
              </div>
            ) : null;
          })()}
        </div>
      )}
      {deleteConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setDeleteConfirm(null)}>
          <div style={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,77,77,0.2)', borderRadius: 16, padding: '28px 32px', maxWidth: 400, width: '90%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,77,77,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ff4d4d" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
            </div>
            <h3 style={{ color: '#e2e8f0', fontSize: 18, fontWeight: 600, margin: '0 0 8px' }}>Remove Holding</h3>
            <p style={{ color: '#94a3b8', fontSize: 14, margin: '0 0 6px', lineHeight: 1.5 }}>
              Are you sure you want to remove <strong style={{ color: '#ff4d4d' }}>{deleteConfirm.ticker}</strong>?
            </p>
            {deleteConfirm.broker && <p style={{ color: '#64748b', fontSize: 12, margin: '0 0 20px' }}>From: {deleteConfirm.broker}</p>}
            {!deleteConfirm.broker && <div style={{ marginBottom: 20 }} />}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '11px', background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 10, color: '#94a3b8', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>Cancel</button>
              <button onClick={handleDelete} style={{ flex: 1, padding: '11px', background: 'rgba(255,77,77,0.15)', border: '1px solid rgba(255,77,77,0.3)', borderRadius: 10, color: '#ff4d4d', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PortfolioManager;
